import { useMemo, useState, useEffect } from 'react';
import styled from '@emotion/styled';
import { mediaQuery } from '@styles/index';
import F1AndF2Filters from './F1AndF2Filters';
import { useTestSeriesData } from './useTestSeriesData';
import CenterSelectionFlow, { useDropdown } from './CenterSelectionFlow';

const FindNearestTestCenterSectionWrapper = styled.div`
  display: flex;
  background-color: var(--color-base-1);
  scroll-margin-top: 100px;
  flex-direction: column;
  gap: 12px;
  justify-content: center;
  align-items: center;
  width: 100%;
  padding: 100px;

  ${mediaQuery.uptoMobile} {
    padding: 40px 5%;
  }
`;

const Title = styled.p`
  font-weight: 700;
  font-size: 64px;
  line-height: 120%;
  letter-spacing: -2%;
  text-align: center;
  color: var(--color-primary-focus);

  ${mediaQuery.uptoMobile} {
    font-size: 40px;
  }
`;

const SubTitle = styled.p`
  font-weight: 400;
  font-size: 32px;
  line-height: 150%;
  letter-spacing: -1px;
  color: var(--color-text-secondary);
  text-align: center;
  margin-bottom: 40px;

  ${mediaQuery.uptoMobile} {
    font-size: 18px;
    margin-bottom: 20px;
  }
`;

const FindNearestTestCenterSection = () => {
  const { data, allPossibleF1, allPossibleF2 } = useTestSeriesData();
  const hybridCards = useMemo(
    () => data?.hybridSkuCard || [],
    [data?.hybridSkuCard]
  );

  // Local F1/F2 state for this section
  const [selectedF1, setSelectedF1] = useState('');
  const [selectedF2, setSelectedF2] = useState('');

  // Set initial F1/F2 on mount or when hybridCards change
  useEffect(() => {
    if (hybridCards.length > 0) {
      setSelectedF1(hybridCards[0]?.f1);
      setSelectedF2(hybridCards[0]?.f2);
    }
  }, [hybridCards]);

  const combinedCsvDetails = useMemo(() => {
    const filteredHybridCardToShow = hybridCards.filter(
      (item) => item.f1 === selectedF1 && item.f2 === selectedF2
    );
    return (
      filteredHybridCardToShow?.map((item) => item.csvDetails).flat() || []
    );
  }, [hybridCards, selectedF1, selectedF2]);

  const {
    stateOptions,
    cityOptions,
    centerOptions,
    selectedState,
    selectedCity,
    selectedCenter,
    setSelectedState,
    setSelectedCity,
    setSelectedCenter
  } = useDropdown({
    centerList: combinedCsvDetails || []
  });

  useEffect(() => {
    setSelectedState(null);
    setSelectedCity(null);
    setSelectedCenter(null);
  }, [
    selectedF1,
    selectedF2,
    setSelectedCenter,
    setSelectedCity,
    setSelectedState
  ]);

  return (
    <FindNearestTestCenterSectionWrapper id="test-center">
      <Title>Find the nearest test center</Title>
      <SubTitle>Select state and city to view centres</SubTitle>
      <F1AndF2Filters
        selectedF1={selectedF1}
        setSelectedF1={setSelectedF1}
        selectedF2={selectedF2}
        setSelectedF2={setSelectedF2}
        allPossibleF1={allPossibleF1}
        allPossibleF2={allPossibleF2}
      />
      <div style={{ height: '10px' }} />
      <CenterSelectionFlow
        type="find-nearest-test-center"
        stateOptions={stateOptions}
        cityOptions={cityOptions}
        centerOptions={centerOptions}
        selectedState={selectedState}
        selectedCity={selectedCity}
        selectedCenter={selectedCenter}
        setSelectedState={setSelectedState}
        setSelectedCity={setSelectedCity}
        setSelectedCenter={setSelectedCenter}
      />
    </FindNearestTestCenterSectionWrapper>
  );
};

export default FindNearestTestCenterSection;
